import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api/client';
import { ORIGINATORS_QUERY_KEY, PEOPLE_QUERY_KEY } from '@/features/pipeline/useEntities';

/** Deal listado na ficha (espelha OriginatorDealItem / PersonDealItem do backend). */
export interface ProfileDealItem {
  dealId: string;
  code: string;
  codename: string;
  currentStage: string;
  auxiliaryStatus: string;
  investmentValueBRL: number | null;
  leadUserName: string | null;
}

/** Métricas da ficha da Originadora (4 colunas do ranking, all-time). */
export interface OriginationMetrics {
  dealsBrought: number;
  reachedIc1Count: number;
  closedCount: number;
  exposureBRL: number;
}

export interface OriginatorProfile {
  id: string;
  name: string;
  typeId: number;
  vegaRelationshipManagerId: string | null;
  summary: string | null;
  eTag: string;
  metrics: OriginationMetrics;
  deals: ProfileDealItem[];
}

export interface PersonProfile {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  currentOriginatorId: string | null;
  role: string | null;
  summary: string | null;
  eTag: string;
  deals: ProfileDealItem[];
}

export function useOriginatorProfile(id: string | null) {
  return useQuery({
    queryKey: ['origination', 'originatorProfile', id],
    queryFn: () => apiFetch<OriginatorProfile>(`/api/originators/${id}/profile`),
    enabled: !!id,
  });
}

export function usePersonProfile(id: string | null) {
  return useQuery({
    queryKey: ['origination', 'personProfile', id],
    queryFn: () => apiFetch<PersonProfile>(`/api/people/${id}/profile`),
    enabled: !!id,
  });
}

/** Atualiza a Originadora via PUT (If-Match com o ETag da ficha → 412 em conflito). */
export function useUpdateOriginatorProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id: string;
      eTag: string;
      name: string;
      typeId: number;
      vegaRelationshipManagerId: string | null;
      summary: string | null;
    }) =>
      apiFetch<unknown>(`/api/originators/${input.id}`, {
        method: 'PUT',
        headers: { 'If-Match': `"${input.eTag}"` },
        body: {
          name: input.name,
          typeId: input.typeId,
          vegaRelationshipManagerId: input.vegaRelationshipManagerId,
          summary: input.summary,
        },
      }),
    onSuccess: (_r, input) => {
      void qc.invalidateQueries({ queryKey: ['origination'] });
      void qc.invalidateQueries({ queryKey: ['origination', 'originatorProfile', input.id] });
      void qc.invalidateQueries({ queryKey: ORIGINATORS_QUERY_KEY });
      void qc.invalidateQueries({ queryKey: ['deals'] });
    },
  });
}

/** Atualiza a Pessoa via PUT (If-Match com o ETag da ficha → 412 em conflito). */
export function useUpdatePersonProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id: string;
      eTag: string;
      name: string;
      email: string | null;
      phone: string | null;
      currentOriginatorId: string | null;
      role: string | null;
      summary: string | null;
    }) =>
      apiFetch<unknown>(`/api/people/${input.id}`, {
        method: 'PUT',
        headers: { 'If-Match': `"${input.eTag}"` },
        body: {
          name: input.name,
          email: input.email,
          phone: input.phone,
          currentOriginatorId: input.currentOriginatorId,
          role: input.role,
          summary: input.summary,
        },
      }),
    onSuccess: (_r, input) => {
      void qc.invalidateQueries({ queryKey: ['origination'] });
      void qc.invalidateQueries({ queryKey: ['origination', 'personProfile', input.id] });
      void qc.invalidateQueries({ queryKey: PEOPLE_QUERY_KEY });
      void qc.invalidateQueries({ queryKey: ['deals'] });
    },
  });
}
